import { Button, Input, Popconfirm, Select, Table } from "antd";
import { Plus, Trash } from "lucide-react";
import { familyDetailRelation } from "../../help-service/enumList";
import { familyDetailsInitialValues } from "./enum";
import trashIcon from "../../images/red-trash-icon.svg";
import { Col, Form, Row } from "react-bootstrap";
import { useEffect } from "react";

const FamilyDetailTable = ({ errors, details, setDetails, loading }) => {
  const handleChange = (index, field, value) => {
    setDetails((prev) => {
      const updatedData = [...prev.data];
      updatedData[index] = {
        ...updatedData[index],
        [field]: value,
      };
      return { ...prev, data: updatedData };
    });
  };

  const columns = [
    {
      title: "",
      dataIndex: "sr_no",
      key: "sr_no",
      width: 3,
      align: "center",
      render: (text, record, index) => (
        <div className="fw-semibold">{index + 1}.</div>
      ),
    },
    {
      title: (
        <span>
          Member Name <span className="text-danger">*</span>
        </span>
      ),
      dataIndex: "itl_name",
      key: "itl_name",
      render: (text, record, index) => (
        <div className="d-flex flex-column align-items-start">
          <Input
            value={text}
            placeholder="Member Name"
            className={`${errors[index]?.itl_name ? "error" : ""}`}
            onChange={(e) => handleChange(index, "itl_name", e.target.value)}
            disabled={loading}
          />
          <div className="input-error">{errors[index]?.itl_name}</div>
        </div>
      ),
    },
    {
      title: (
        <span>
          Relation <span className="text-danger">*</span>
        </span>
      ),
      dataIndex: "itl_relation",
      key: "itl_relation",
      render: (text, record, index) => (
        <div className="d-flex flex-column align-items-start">
          <Select
            value={text}
            style={{ width: "100%" }}
            placeholder="Relation"
            className={`${errors[index]?.itl_relation ? "error" : ""}`}
            onChange={(value) => handleChange(index, "itl_relation", value)}
            options={familyDetailRelation.list}
            disabled={loading}
          />
          <div className="input-error">{errors[index]?.itl_relation}</div>
        </div>
      ),
    },
    {
      title: (
        <span>
          Profession <span className="text-danger">*</span>
        </span>
      ),
      dataIndex: "itl_profession",
      key: "itl_profession",
      render: (text, record, index) => (
        <div className="d-flex flex-column align-items-start">
          <Input
            value={text}
            placeholder="Profession"
            className={`${errors[index]?.itl_profession ? "error" : ""}`}
            onChange={(e) =>
              handleChange(index, "itl_profession", e.target.value)
            }
            disabled={loading}
          />
          <div className="input-error">{errors[index]?.itl_profession}</div>
        </div>
      ),
    },
    {
      title: "Remove",
      dataIndex: "remove",
      key: "remove",
      fixed: "right",
      width: 50,
      render: (text, record, index) =>
        details?.data?.length > 1 ? (
          <div className="d-flex justify-content-center">
            <Popconfirm
              title={`Remove ${record.itl_name}?`}
              onConfirm={() => handleDelete(index)}
              okText="Yes"
              cancelText="No"
              disabled={details?.data?.length === 1}
            >
              <Trash
                className="text-danger cursor-pointer"
                style={{ height: "20px", width: "20px" }}
              />
            </Popconfirm>
          </div>
        ) : (
          <div className="d-flex justify-content-center">
            <Trash
              className="text-secondary cursor-not-allowed"
              style={{ height: "20px", width: "20px" }}
            />
          </div>
        ),
    },
  ];

  const handleDelete = (index) => {
    setDetails((prev) => {
      const updatedData = [...prev.data];
      const deletedItem = updatedData[index];
      updatedData.splice(index, 1);

      const updatedDeletedData = [...prev.deletedData];
      if (deletedItem?.itl_familydetailid) {
        updatedDeletedData.push(deletedItem.itl_familydetailid);
      }

      return {
        ...prev,
        data: updatedData,
        deletedData: updatedDeletedData,
      };
    });
  };

  const handleAddNewRow = () => {
    setDetails((prev) => ({
      ...prev,
      data: [...prev.data, { ...familyDetailsInitialValues }],
    }));
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-end mb-3">
        <h5 className="mb-0">Family Details</h5>

        <Button
          type="primary"
          onClick={handleAddNewRow}
          disabled={loading}
          className="d-none d-lg-flex btn-blue border-none"
        >
          <Plus style={{ height: "15px", width: "15px" }} />
          <div> Add Member</div>
        </Button>
      </div>

      <hr />

      <div className="d-none d-lg-block">
        <Table
          size="small"
          columns={columns}
          dataSource={details.data}
          pagination={false}
          scroll={{ x: "max-content" }}
          className="custom-candidate-form-table"
        />
      </div>

      {/* Mobile view */}
      <div className="d-lg-none">
        {details.data.map((item, index) => (
          <div key={index}>
            {details.data.length > 1 && (
              <div className="d-flex justify-content-end">
                <div className="delete-btn" onClick={() => handleDelete(index)}>
                  <img src={trashIcon} alt="trash" />
                </div>
              </div>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="required fw-semibold">
                    {index + 1}. Member Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Member Name"
                    value={item.itl_name}
                    className={`${errors[index]?.itl_name ? "error" : ""}`}
                    onChange={(e) =>
                      handleChange(index, "itl_name", e.target.value)
                    }
                    disabled={loading}
                  />
                  <div className="input-error">{errors[index]?.itl_name}</div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="required fw-semibold">
                    Relation
                  </Form.Label>
                  <Form.Select
                    value={item.itl_relation}
                    className={`${errors[index]?.itl_relation ? "error" : ""}`}
                    onChange={(e) =>
                      handleChange(index, "itl_relation", e.target.value)
                    }
                    disabled={loading}
                  >
                    <option value="" disabled>Select Relation</option>
                    {familyDetailRelation.list.map((rel) => (
                      <option key={rel.value} value={rel.value}>
                        {rel.label}
                      </option>
                    ))}
                  </Form.Select>
                  <div className="input-error">
                    {errors[index]?.itl_relation}
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="required fw-semibold">
                    Profession
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Profession"
                    value={item.itl_profession}
                    className={`${errors[index]?.itl_profession ? "error" : ""
                      }`}
                    onChange={(e) =>
                      handleChange(index, "itl_profession", e.target.value)
                    }
                    disabled={loading}
                  />
                  <div className="input-error">
                    {errors[index]?.itl_profession}
                  </div>
                </Form.Group>
              </Col>
            </Row>
            {index < details.data.length - 1 && <hr />}
          </div>
        ))}

        <div className="d-flex justify-content-center mt-4">
          <Button
            className="me-3 btn-blue border-none"
            type="primary"
            onClick={handleAddNewRow}
            disabled={loading}
          >
            <Plus style={{ height: "15px", width: "15px" }} />
            <div>Add Member</div>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FamilyDetailTable;
